import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Divider,
  Alert,
  LinearProgress
} from '@mui/material';
import DoorFrontOutlinedIcon from '@mui/icons-material/DoorFrontOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import SignalWifi4BarIcon from '@mui/icons-material/SignalWifi4Bar';
import WarningIcon from '@mui/icons-material/Warning';
import BuildIcon from '@mui/icons-material/Build';
import * as d3 from 'd3';
import { getDoorByName, generateDoorMockData } from '../../data/doorMockData';

const DoorEntityDrawer = ({ entityDetails, chartRef }) => {
  const [selectedDoor, setSelectedDoor] = useState(null);
  const [allDoorsData, setAllDoorsData] = useState(null);

  useEffect(() => {
    // Generate all doors data
    const doorsData = generateDoorMockData();
    setAllDoorsData(doorsData);

    let foundDoor = null;

    // Find the specific door based on entityDetails name
    if (entityDetails && entityDetails.name) {
      foundDoor = doorsData.doors.find(door => door.name === entityDetails.name);
      
      // If not found by exact name, try MAC address from keys as fallback
      if (!foundDoor && entityDetails.keys) {
        const macAddressKey = entityDetails.keys.find(key => key.keyName === 'mac_address');
        if (macAddressKey) {
          foundDoor = getDoorByName(macAddressKey.value);
        }
      }
    }
    
    // Fallback to first door if no specific door found
    if (!foundDoor && doorsData.doors.length > 0) {
      foundDoor = doorsData.doors[0];
    }

    setSelectedDoor(foundDoor);
  }, [entityDetails]);

  const renderOpenDurationChart = useCallback(() => {
    if (!selectedDoor || !chartRef.current) return;
    d3.select(chartRef.current).selectAll('*').remove();

    const width = 400;
    const height = 300;
    const margin = { top: 20, right: 20, bottom: 60, left: 60 };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    const svg = d3.select(chartRef.current)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    const data = selectedDoor.openDuration7d;

    const xScale = d3.scaleBand()
      .domain(data.map(d => d.day))
      .range([0, chartWidth])
      .padding(0.2);

    const yScale = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.minutes)])
      .nice()
      .range([chartHeight, 0]);

    svg.append('g')
      .attr('transform', `translate(0, ${chartHeight})`)
      .call(d3.axisBottom(xScale))
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-45)')
      .style('font-size', '10px');

    svg.append('g')
      .call(d3.axisLeft(yScale))
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -40)
      .attr('x', -chartHeight / 2)
      .attr('text-anchor', 'middle')
      .text('Open Duration (min)')
      .style('font-size', '12px');

    const bars = svg.selectAll('.bar')
      .data(data)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', d => xScale(d.day))
      .attr('y', d => yScale(d.minutes))
      .attr('width', xScale.bandwidth())
      .attr('height', d => chartHeight - yScale(d.minutes))
      .attr('fill', '#9C27B0')
      .style('cursor', 'pointer');

    // Tooltip
    const tooltip = d3.select(chartRef.current)
      .append('div')
      .attr('class', 'tooltip')
      .style('opacity', 0)
      .style('position', 'absolute')
      .style('background', 'rgba(0, 0, 0, 0.8)')
      .style('color', 'white')
      .style('padding', '8px')
      .style('border-radius', '4px')
      .style('font-size', '12px')
      .style('pointer-events', 'none');

    bars.on('mouseover', function(event, d) {
      d3.select(this).attr('fill', '#7B1FA2');
      tooltip.transition().duration(200).style('opacity', 0.9);
      tooltip.html(`<strong>${d.day}</strong><br/>Open: ${d.minutes} min`)
        .style('left', (event.pageX + 10) + 'px')
        .style('top', (event.pageY - 28) + 'px');
    }).on('mouseout', function() {
      d3.select(this).attr('fill', '#9C27B0');
      tooltip.transition().duration(300).style('opacity', 0);
    });

    svg.append('text')
      .attr('x', chartWidth / 2)
      .attr('y', -5)
      .attr('text-anchor', 'middle')
      .style('font-size', '14px')
      .style('font-weight', 'bold')
      .text('Door Open Duration - Last 7 Days');
  }, [selectedDoor]);

  useEffect(() => {
    if (chartRef.current && selectedDoor && selectedDoor.openDuration7d.length > 0) {
      renderOpenDurationChart();
    }
  }, [selectedDoor, renderOpenDurationChart]);

  // Show loading state if no door data
  if (!selectedDoor || !allDoorsData) {
    return (
      <Box sx={{ p: 2 }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 1 }}>
          Loading door sensor data...
        </Typography>
      </Box>
    );
  }

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'warning';
      case 'Closed': return 'success';
      default: return 'primary';
    }
  };

  const getMaintenanceColor = (status) => {
    switch (status) {
      case 'Needs Attention': return 'warning';
      case 'Good': return 'success';
      default: return 'primary';
    }
  };

  return (
    <>
      {/* Door Sensor Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Box display="flex" alignItems="center">
              <DoorFrontOutlinedIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Box>
                <Typography variant="h6">
                  {selectedDoor.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {selectedDoor.location}
                </Typography>
              </Box>
            </Box>
            <Box display="flex" gap={1}>
              <Chip 
                label={selectedDoor.status} 
                color={getStatusColor(selectedDoor.status)}
                variant="outlined" 
              />
              <Chip 
                label={selectedDoor.isLocked ? 'Locked' : 'Unlocked'} 
                color={selectedDoor.isLocked ? 'success' : 'error'}
                variant="outlined" 
              />
            </Box>
          </Box>

          {/* Status Alert */}
          {(selectedDoor.status === 'Open' || !selectedDoor.isLocked) && (
            <Alert severity={selectedDoor.status === 'Open' ? 'warning' : 'error'} sx={{ mb: 2 }}>
              {selectedDoor.status === 'Open' && 'Door is currently open. Monitor for security.'}
              {!selectedDoor.isLocked && 'Door is unlocked. Check security protocols.'}
            </Alert>
          )}
          
          {/* Door Metrics Summary */}
          <Divider sx={{ my: 2 }} />
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Door Activity Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Box textAlign="center">
                  <Typography variant="h6" color="primary">
                    {selectedDoor.metrics.dailyOpenings}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Daily Openings
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box textAlign="center">
                  <Typography variant="h6" color="info.main">
                    {selectedDoor.metrics.avgOpenDuration}s
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Avg Duration
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box textAlign="center">
                  <Typography variant="h6" color="secondary.main">
                    {selectedDoor.metrics.totalOpenMinutes}m
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Total Open Time
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box textAlign="center">
                  <Typography 
                    variant="h6" 
                    color={selectedDoor.metrics.usagePattern === 'High' ? 'error.main' : 
                           selectedDoor.metrics.usagePattern === 'Medium' ? 'warning.main' : 'success.main'}
                  >
                    {selectedDoor.metrics.usagePattern}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Usage Pattern
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Door Details */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            Door Information
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <DoorFrontOutlinedIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Door Type" 
                secondary={`${selectedDoor.doorType} - ${selectedDoor.accessLevel}`} 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <AccessTimeIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Last Opened" 
                secondary={new Date(selectedDoor.lastOpenedAt).toLocaleString()} 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <SecurityIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Security" 
                secondary={`${selectedDoor.security.lockType} - ${selectedDoor.security.accessControl}`} 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <BatteryFullIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Sensor Battery" 
                secondary={`${selectedDoor.maintenance.batteryLevel}%`} 
              />
            </ListItem>
          </List>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" gutterBottom>
            System Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {allDoorsData.summary.totalDoors}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Total Doors
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box textAlign="center">
                <Typography variant="h6" color="warning.main">
                  {allDoorsData.summary.openDoors}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Open Doors
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box textAlign="center">
                <Typography variant="h6" color="success.main">
                  {allDoorsData.summary.lockedDoors}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Locked Doors
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box textAlign="center">
                <Typography variant="h6" color="error.main">
                  {allDoorsData.summary.securityAlerts}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Security Alerts
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Usage Trends Chart */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
            <LockOutlinedIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              Usage Trends
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            7-day door usage showing opening patterns and duration metrics.
          </Typography>
          <Box
            ref={chartRef}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '320px'
            }}
          />
        </CardContent>
      </Card>

      {/* Maintenance & Security Alerts */}
      {(selectedDoor.maintenance.maintenanceStatus === 'Needs Attention' || 
        selectedDoor.maintenance.sensorStatus === 'Offline' ||
        selectedDoor.security.securityAlerts.length > 0 ||
        selectedDoor.maintenance.batteryLevel < 20) && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
              <WarningIcon sx={{ mr: 1, color: 'warning.main' }} />
              <Typography variant="h6">
                Alerts & Maintenance
              </Typography>
            </Box>
            <List dense>
              {selectedDoor.maintenance.maintenanceStatus === 'Needs Attention' && (
                <ListItem>
                  <ListItemIcon>
                    <BuildIcon color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Maintenance Required"
                    secondary="Door sensor requires maintenance attention."
                  />
                </ListItem>
              )}
              {selectedDoor.maintenance.sensorStatus === 'Offline' && (
                <ListItem>
                  <ListItemIcon>
                    <WarningIcon color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Sensor Offline"
                    secondary="Door sensor is not responding. Check connectivity and power."
                  />
                </ListItem>
              )}
              {selectedDoor.maintenance.batteryLevel < 20 && (
                <ListItem>
                  <ListItemIcon>
                    <BatteryFullIcon color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Low Battery"
                    secondary={`Battery level is ${selectedDoor.maintenance.batteryLevel}%. Replace battery soon.`}
                  />
                </ListItem>
              )}
              {selectedDoor.security.securityAlerts.map((alert, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <SecurityIcon color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Security Alert"
                    secondary={alert}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default DoorEntityDrawer;


